import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../api/auth';
import type { User, Tenant } from '../types/api';

interface AuthResponse {
  user: User;
  token: string;
}

interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount, try to restore session from the httpOnly cookie
  // by calling GET /auth/me. If the cookie is valid the server
  // returns the user; if not we stay logged out.
  useEffect(() => {
    const loadUser = async () => {
      try {
        const data = await authApi.getCurrentUser();
        setUser(data.user);
        setTenant(data.tenant || null);
      } catch {
        // No valid cookie — user is simply not authenticated
        setUser(null);
        setTenant(null);
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authApi.login({ email, password });
    // The server has already set an httpOnly cookie.
    // We only need to store the user in React state.
    setUser(response.user);
    setTenant(response.tenant || null);
    return response;
  };

  const logout = async () => {
    // Mark driver as offline before clearing the session
    if (user?.role === 'driver') {
      try {
        const { default: axiosInstance } = await import('../api/axios');
        await axiosInstance.post('/driver/logout');
      } catch (_) { /* best-effort */ }
    }
    // Clear the httpOnly cookie on the server
    try {
      await authApi.logout();
    } catch (_) { /* best-effort */ }
    setUser(null);
    setTenant(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        token: null, // Token is now in an httpOnly cookie, not accessible via JS
        loading,
        login,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
